import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';

const Vision = () => {
  const [activeView, setActiveView] = useState('technical');
  const technicalTableRef = useRef(null);
  const businessTableRef = useRef(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Table copied to clipboard! You can now paste it into PowerPoint.');
  };

  const downloadTableAsPNG = async (tableRef, filename) => {
    if (tableRef.current) {
      try {
        const canvas = await html2canvas(tableRef.current, {
          backgroundColor: '#1a1a1a',
          scale: 2,
          logging: false,
          useCORS: true
        });
        
        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL();
        link.click();
      } catch (error) {
        console.error('Error generating PNG:', error);
      }
    }
  };

  const technicalRoadmapData = `TIMELINE\tEVOML CAPABILITIES\tARTEMIS CAPABILITIES\tINTEGRATED VALUE
Q1 2025 - Foundation\t• Automated data prep with Artemis code generation\n• 100+ ML algorithms tested in parallel\n• Model governance with validation reports\n• One-click deployment with production code\n• Multi-agent integration (Vertex AI, Claude Code)\n• MLOps monitoring with continuous optimization\t• Automated code optimization (up to 10x)\n• Security vulnerability detection\n• Legacy code modernization\n• Performance bottleneck analysis\n• Multi-objective optimization\n• Agent integration for code evolution\t• Complete end-to-end ML automation\n• Agent orchestration ecosystem\n• Production-ready enterprise platform\n• Zero-dependency deployment\n• Full audit trails & compliance

Q2 2025 - Enhanced Intelligence\t• Advanced GenAI integration for insights\n• Automated audit report generation\n• Conversational interfaces for data exploration\n• Intelligent feature recommendation\n• Enhanced model explainability\n• Real-time agent performance monitoring\t• Enhanced agent coordination\n• Real-time code suggestions\n• CI/CD pipeline automation\n• IDE integration (VS Code, IntelliJ)\n• Automated refactoring\n• Agent governance frameworks\t• Natural language ML development\n• Intelligent automation throughout\n• Enhanced deployment workflows\n• Predictive model monitoring\n• Complete transparency & governance

Q3 2025 - AI Data Scientist\t• Unsupervised learning (clustering, anomaly)\n• Individual explainability (SHAP, LIME)\n• Conversational AI data scientist\n• Synthetic data generation\n• MCP server capabilities\n• Agentic ML orchestration\t• Advanced agentic capabilities\n• Autonomous code coordination\n• Cross-platform optimization\n• Self-organizing architectures\n• Predictive maintenance\n• Intelligent agent swarms\t• World-class AI data scientist\n• Complete ML capabilities\n• Talk to your data naturally\n• Self-improving workflows\n• Proactive optimization

Q4 2025+ - Revolutionary Ecosystem\t• Dataset-to-App automation\n• Hidden pattern & correlation discovery\n• Ready-to-use ML models generation\n• Universal data integration via MCP\n• ML ecosystem connectivity\n• Automated insight generation\t• Autonomous optimization\n• Self-healing systems\n• Architecture evolution\n• Cross-language capabilities\n• Intelligent coordination\n• Full automation stack\t• Upload data → Complete app\n• Zero manual intervention\n• Self-evolving platform\n• Unified ML ecosystem\n• Continuous innovation`;

  const businessValueData = `USE CASE\tCURRENT CHALLENGES\tWITH EVOML & ARTEMIS\tEXPECTED IMPACT
Fraud Detection\t• Manual feature engineering\n• Limited model types\n• Slow pattern detection\n• High false positives\t• Automated feature discovery\n• 100+ algorithms tested\n• Real-time anomaly detection\n• Optimized precision/recall\t• 50% reduction in fraud losses\n• 70% fewer false positives\n• Minutes vs days for patterns\n• Better customer experience

Risk & Compliance\t• Time-consuming validation\n• Manual documentation\n• Periodic reviews\n• Siloed risk models\t• Automated governance\n• Real-time compliance\n• Continuous monitoring\n• Integrated framework\t• 80% faster reporting\n• Lower compliance costs\n• Better risk visibility\n• Audit-ready documentation

Customer Analytics\t• Basic segmentation\n• Limited personalization\n• Reactive insights\n• Channel silos\t• Behavioral modeling\n• Hyper-personalization\n• Predictive analytics\n• Omnichannel optimization\t• 3x conversion improvement\n• 40% churn reduction\n• Higher lifetime value\n• Personalized products

Trading Systems\t• Legacy infrastructure\n• Manual optimization\n• Performance issues\n• High latency\t• Automated modernization\n• AI-powered optimization\n• Real-time performance\n• Low-latency execution\t• 60% latency reduction\n• Better execution quality\n• Reduced operational risk\n• Lower infrastructure costs

Operations\t• Manual processes\n• Technical debt\n• Slow deployments\n• High maintenance\t• Process automation\n• Code modernization\n• Automated deployment\n• Self-maintaining systems\t• 70% faster delivery\n• 50% cost reduction\n• Improved reliability\n• Focus on innovation`;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#1a1a1a',
      color: 'white',
      fontFamily: 'Arial, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '36px', marginBottom: '10px' }}>
            EvoML & Artemis: Vision & Roadmap
          </h1>
          <p style={{ fontSize: '18px', color: '#999' }}>
            AI-Powered Development Platform for Financial Services
          </p>
        </div>

        {/* View Toggle */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <button
            onClick={() => setActiveView('technical')}
            style={{
              padding: '10px 30px',
              marginRight: '10px',
              background: activeView === 'technical' ? '#4a5eff' : '#333',
              border: 'none',
              borderRadius: '5px',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Technical Roadmap
          </button>
          <button
            onClick={() => setActiveView('business')}
            style={{
              padding: '10px 30px',
              background: activeView === 'business' ? '#4a5eff' : '#333',
              border: 'none',
              borderRadius: '5px',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Business Value
          </button>
        </div>

        {activeView === 'technical' && (
          <div>
            <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>Technical Roadmap</h2>
            
            {/* Simple Table */}
            <div ref={technicalTableRef} style={{ 
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
              marginBottom: '30px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(90deg, #4a5eff 0%, #3b82f6 100%)' }}>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #2563eb',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '15%' 
                    }}>
                      Timeline
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #2563eb',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '35%' 
                    }}>
                      EvoML Capabilities
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #2563eb',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '35%' 
                    }}>
                      Artemis Capabilities
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #2563eb',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '15%' 
                    }}>
                      Integrated Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: 'rgba(74, 94, 255, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(74, 94, 255, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #4a5eff'
                    }}>
                      <strong style={{ color: '#4a5eff', fontSize: '16px' }}>Q1 2025</strong><br/>
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>Foundation</span>
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(74, 94, 255, 0.2)', verticalAlign: 'top' }}>
                      • Automated data prep with Artemis code generation<br/>
                      • 100+ ML algorithms tested in parallel<br/>
                      • Model governance with validation reports<br/>
                      • One-click deployment with production code<br/>
                      • Multi-agent integration (Vertex AI, Claude Code)<br/>
                      • MLOps monitoring with continuous optimization
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(74, 94, 255, 0.2)', verticalAlign: 'top' }}>
                      • Automated code optimization (up to 10x)<br/>
                      • Security vulnerability detection<br/>
                      • Legacy code modernization<br/>
                      • Performance bottleneck analysis<br/>
                      • Multi-objective optimization<br/>
                      • Agent integration for code evolution
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(74, 94, 255, 0.2)', verticalAlign: 'top' }}>
                      • Complete end-to-end ML automation<br/>
                      • Agent orchestration ecosystem<br/>
                      • Production-ready enterprise platform<br/>
                      • Zero-dependency deployment<br/>
                      • Full audit trails & compliance
                    </td>
                  </tr>
                  <tr>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(255, 165, 0, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #ffa500'
                    }}>
                      <strong style={{ color: '#ffa500', fontSize: '16px' }}>Q2 2025</strong><br/>
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>Enhanced Intelligence</span>
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(255, 165, 0, 0.2)', verticalAlign: 'top' }}>
                      • Advanced GenAI integration for insights<br/>
                      • Automated audit report generation<br/>
                      • Conversational interfaces for data exploration<br/>
                      • Intelligent feature recommendation<br/>
                      • Enhanced model explainability<br/>
                      • Real-time agent performance monitoring
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(255, 165, 0, 0.2)', verticalAlign: 'top' }}>
                      • Enhanced agent coordination<br/>
                      • Real-time code suggestions<br/>
                      • CI/CD pipeline automation<br/>
                      • IDE integration (VS Code, IntelliJ)<br/>
                      • Automated refactoring<br/>
                      • Agent governance frameworks
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(255, 165, 0, 0.2)', verticalAlign: 'top' }}>
                      • Natural language ML development<br/>
                      • Intelligent automation throughout<br/>
                      • Enhanced deployment workflows<br/>
                      • Predictive model monitoring<br/>
                      • Complete transparency & governance
                    </td>
                  </tr>
                  <tr style={{ background: 'rgba(139, 92, 246, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
                      verticalAlign: 'top',
                      borderLeft: '4px solid #8b5cf6'
                    }}>
                      <strong style={{ color: '#8b5cf6', fontSize: '16px' }}>Q3 2025</strong><br/>
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>AI Data Scientist</span>
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(139, 92, 246, 0.2)', verticalAlign: 'top' }}>
                      • Unsupervised learning (clustering, anomaly)<br/>
                      • Individual explainability (SHAP, LIME)<br/>
                      • Conversational AI data scientist<br/>
                      • Synthetic data generation<br/>
                      • MCP server capabilities<br/>
                      • Agentic ML orchestration
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(139, 92, 246, 0.2)', verticalAlign: 'top' }}>
                      • Advanced agentic capabilities<br/>
                      • Autonomous code coordination<br/>
                      • Cross-platform optimization<br/>
                      • Self-organizing architectures<br/>
                      • Predictive maintenance<br/>
                      • Intelligent agent swarms
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(139, 92, 246, 0.2)', verticalAlign: 'top' }}>
                      • World-class AI data scientist<br/>
                      • Complete ML capabilities<br/>
                      • Talk to your data naturally<br/>
                      • Self-improving workflows<br/>
                      • Proactive optimization
                    </td>
                  </tr>
                  <tr style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #10b981'
                    }}>
                      <strong style={{ color: '#10b981', fontSize: '16px' }}>Q4 2025+</strong><br/>
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>Revolutionary Ecosystem</span>
                    </td>
                    <td style={{ padding: '25px 20px', verticalAlign: 'top' }}>
                      • Dataset-to-App automation<br/>
                      • Hidden pattern & correlation discovery<br/>
                      • Ready-to-use ML models generation<br/>
                      • Universal data integration via MCP<br/>
                      • ML ecosystem connectivity<br/>
                      • Automated insight generation
                    </td>
                    <td style={{ padding: '25px 20px', verticalAlign: 'top' }}>
                      • Autonomous optimization<br/>
                      • Self-healing systems<br/>
                      • Architecture evolution<br/>
                      • Cross-language capabilities<br/>
                      • Intelligent coordination<br/>
                      • Full automation stack
                    </td>
                    <td style={{ padding: '25px 20px', verticalAlign: 'top' }}>
                      • Upload data → Complete app<br/>
                      • Zero manual intervention<br/>
                      • Self-evolving platform<br/>
                      • Unified ML ecosystem<br/>
                      • Continuous innovation
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => copyToClipboard(technicalRoadmapData)}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #4a5eff, #3b82f6)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(74, 94, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📋 Copy Table
              </button>
              <button
                onClick={() => downloadTableAsPNG(technicalTableRef, 'technical-roadmap.png')}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📥 Download PNG
              </button>
            </div>
          </div>
        )}

        {activeView === 'business' && (
          <div>
            <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>Business Value for Banking</h2>
            
            {/* Simple Table */}
            <div ref={businessTableRef} style={{ 
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
              marginBottom: '30px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)' }}>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #047857',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '20%' 
                    }}>
                      Use Case
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #047857',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '30%' 
                    }}>
                      Current Challenges
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #047857',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '30%' 
                    }}>
                      With EvoML & Artemis
                    </th>
                    <th style={{ 
                      padding: '20px 15px', 
                      textAlign: 'left', 
                      borderBottom: '3px solid #047857',
                      fontSize: '16px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      width: '20%' 
                    }}>
                      Expected Impact
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(239, 68, 68, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #ef4444'
                    }}>
                      <strong style={{ color: '#ef4444', fontSize: '15px' }}>Fraud Detection</strong>
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', verticalAlign: 'top' }}>
                      • Manual feature engineering<br/>
                      • Limited model types<br/>
                      • Slow pattern detection<br/>
                      • High false positives
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', verticalAlign: 'top' }}>
                      • Automated feature discovery<br/>
                      • 100+ algorithms tested<br/>
                      • Real-time anomaly detection<br/>
                      • Optimized precision/recall
                    </td>
                    <td style={{ padding: '25px 20px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', verticalAlign: 'top' }}>
                      • 50% less fraud losses<br/>
                      • 70% fewer false positives<br/>
                      • Minutes vs days<br/>
                      • Better CX
                    </td>
                  </tr>
                  <tr>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #3b82f6'
                    }}>
                      <strong style={{ color: '#3b82f6', fontSize: '15px' }}>Risk & Compliance</strong>
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Time-consuming validation<br/>
                      • Manual documentation<br/>
                      • Periodic reviews<br/>
                      • Siloed risk models
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Automated governance<br/>
                      • Real-time compliance<br/>
                      • Continuous monitoring<br/>
                      • Integrated framework
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • 80% faster reporting<br/>
                      • Lower costs<br/>
                      • Better visibility<br/>
                      • Audit-ready
                    </td>
                  </tr>
                  <tr style={{ background: 'rgba(251, 191, 36, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(251, 191, 36, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #fbbf24'
                    }}>
                      <strong style={{ color: '#fbbf24', fontSize: '15px' }}>Customer Analytics</strong>
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Basic segmentation<br/>
                      • Limited personalization<br/>
                      • Reactive insights<br/>
                      • Channel silos
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Behavioral modeling<br/>
                      • Hyper-personalization<br/>
                      • Predictive analytics<br/>
                      • Omnichannel optimization
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • 3x conversion<br/>
                      • 40% less churn<br/>
                      • Higher CLV<br/>
                      • Personal products
                    </td>
                  </tr>
                  <tr>
                    <td style={{ 
                      padding: '25px 20px', 
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #8b5cf6'
                    }}>
                      <strong style={{ color: '#8b5cf6', fontSize: '15px' }}>Trading Systems</strong>
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Legacy infrastructure<br/>
                      • Manual optimization<br/>
                      • Performance issues<br/>
                      • High latency
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • Automated modernization<br/>
                      • AI-powered optimization<br/>
                      • Real-time performance<br/>
                      • Low-latency execution
                    </td>
                    <td style={{ padding: '20px', borderBottom: '1px solid #444', verticalAlign: 'top' }}>
                      • 60% less latency<br/>
                      • Better execution<br/>
                      • Lower risk<br/>
                      • Cost savings
                    </td>
                  </tr>
                  <tr style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
                    <td style={{ 
                      padding: '25px 20px', 
                      verticalAlign: 'top',
                      borderLeft: '4px solid #10b981'
                    }}>
                      <strong style={{ color: '#10b981', fontSize: '15px' }}>Operations</strong>
                    </td>
                    <td style={{ padding: '20px', verticalAlign: 'top' }}>
                      • Manual processes<br/>
                      • Technical debt<br/>
                      • Slow deployments<br/>
                      • High maintenance
                    </td>
                    <td style={{ padding: '20px', verticalAlign: 'top' }}>
                      • Process automation<br/>
                      • Code modernization<br/>
                      • Automated deployment<br/>
                      • Self-maintaining systems
                    </td>
                    <td style={{ padding: '20px', verticalAlign: 'top' }}>
                      • 70% faster delivery<br/>
                      • 50% cost reduction<br/>
                      • Better reliability<br/>
                      • Innovation focus
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => copyToClipboard(businessValueData)}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #4a5eff, #3b82f6)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(74, 94, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📋 Copy Table
              </button>
              <button
                onClick={() => downloadTableAsPNG(businessTableRef, 'business-value.png')}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                📥 Download PNG
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        <div style={{ 
          marginTop: '60px', 
          padding: '40px',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '12px',
          border: '2px solid rgba(74, 94, 255, 0.2)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
        }}>
          <h3 style={{ fontSize: '24px', marginBottom: '20px' }}>Key Takeaways</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '30px' }}>
            <div>
              <h4 style={{ color: '#4a5eff', marginBottom: '10px' }}>EvoML Platform</h4>
              <p style={{ lineHeight: '1.6' }}>
                Automates the entire ML lifecycle from data preparation to deployment. 
                Tests 100+ algorithms automatically, ensures governance compliance, and 
                generates production-ready code with zero dependencies.
              </p>
            </div>
            <div>
              <h4 style={{ color: '#4a5eff', marginBottom: '10px' }}>Artemis Engine</h4>
              <p style={{ lineHeight: '1.6' }}>
                Optimizes existing code for performance, security, and maintainability. 
                Modernizes legacy systems, reduces technical debt, and provides up to 
                10x performance improvements automatically.
              </p>
            </div>
          </div>
          <p style={{ marginTop: '20px', fontSize: '16px', textAlign: 'center', color: '#999' }}>
            Together, they enable banks to innovate faster while maintaining security, compliance, and performance standards.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Vision;